import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  FactCheck as FactCheckIcon,
} from '@mui/icons-material';
import {
  BOOKING_DRAWER_ACCENT,
  getBookingDrawerTheme,
  getGuestDrawerMetaChipSx,
} from '../../utils/bookingDrawerLayout';
import {
  formatBookingAuditTime,
  getBookingAuditActorChipSx,
  getBookingAuditDrawerHeaderSx,
  getBookingAuditDrawerPaperSx,
  getBookingAuditEmptyStateSx,
  getBookingAuditEventCardSx,
  getBookingAuditEventPresentation,
  getBookingAuditEventTypeChipSx,
  getBookingAuditMetaChipSx,
  getBookingAuditRefreshButtonSx,
  getBookingAuditRefreshRowSx,
  getBookingAuditTimelineAvatarSx,
  getBookingAuditTimelineConnectorSx,
  sanitizeBookingAuditActor,
  sanitizeBookingAuditText,
} from '../../utils/bookingAuditLayout';
import { getBookingAuditEvents } from '../../services/api';

const BookingAuditDrawer = ({ open, onClose, bookingId, bookingCode, isDark }) => {
  const drawerTheme = getBookingDrawerTheme(Boolean(isDark));
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState(null);

  const safeBookingId = useMemo(() => {
    const parsedId = Number(bookingId);
    return Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null;
  }, [bookingId]);

  const fetchBookingAuditEvents = useCallback(async () => {
    if (!safeBookingId) {
      setAuditError('Invalid booking reference for audit timeline.');
      setAuditEvents([]);
      return;
    }

    setAuditLoading(true);
    setAuditError(null);
    try {
      const auditData = await getBookingAuditEvents(safeBookingId, { order: 'asc' });
      setAuditEvents(Array.isArray(auditData?.events) ? auditData.events : []);
    } catch (err) {
      setAuditError(err?.message || 'Failed to load booking audit events.');
      setAuditEvents([]);
    } finally {
      setAuditLoading(false);
    }
  }, [safeBookingId]);

  useEffect(() => {
    if (open) {
      fetchBookingAuditEvents();
      return;
    }
    setAuditError(null);
    setAuditEvents([]);
  }, [open, fetchBookingAuditEvents]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: getBookingAuditDrawerPaperSx(isDark),
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={getBookingAuditDrawerHeaderSx(isDark)}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <FactCheckIcon sx={{ color: BOOKING_DRAWER_ACCENT, fontSize: 24, flexShrink: 0 }} />
            <Typography
              variant="h6"
              sx={{ color: isDark ? drawerTheme.text : '#0f172a', fontWeight: 700 }}
            >
              Booking Audit
            </Typography>
            {(bookingCode || safeBookingId) && (
              <Chip
                size="small"
                label={bookingCode || `#${safeBookingId}`}
                sx={getGuestDrawerMetaChipSx(isDark)}
              />
            )}
          </Box>
          <IconButton onClick={onClose} sx={{ color: isDark ? drawerTheme.subtleText : '#64748b' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={getBookingAuditRefreshRowSx(isDark)}>
          <Button
            variant="text"
            onClick={fetchBookingAuditEvents}
            disabled={auditLoading}
            sx={getBookingAuditRefreshButtonSx(isDark)}
          >
            {auditLoading ? 'Refreshing timeline...' : 'Refresh timeline'}
          </Button>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {auditLoading && (
            <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={28} sx={{ color: BOOKING_DRAWER_ACCENT }} />
            </Box>
          )}

          {!auditLoading && auditError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {auditError}
            </Alert>
          )}

          {!auditLoading && !auditError && auditEvents.length === 0 && (
            <Paper elevation={0} sx={getBookingAuditEmptyStateSx(isDark)}>
              <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#64748b' }}>
                No booking activity found yet.
              </Typography>
            </Paper>
          )}

          {!auditLoading &&
            !auditError &&
            auditEvents.map((event, index) => {
              const presentation = getBookingAuditEventPresentation(event.event_type);
              const isLast = index === auditEvents.length - 1;
              const actorUser = event?.payload?.actor_user || null;
              const actorLabel = sanitizeBookingAuditActor(
                actorUser?.full_name || actorUser?.username || actorUser?.email || event.created_by || 'system'
              );
              const actorMeta = actorUser?.email && actorUser?.email !== actorLabel ? actorUser.email : null;

              return (
                <Box key={event.id || `${event.event_type}-${index}`} sx={{ display: 'flex', gap: 1.5, pb: isLast ? 0 : 2 }}>
                  <Box sx={{ width: 28, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Avatar sx={getBookingAuditTimelineAvatarSx(isDark, presentation.color)}>
                      {presentation.icon}
                    </Avatar>
                    {!isLast && <Box sx={getBookingAuditTimelineConnectorSx(isDark)} />}
                  </Box>

                  <Paper elevation={0} sx={getBookingAuditEventCardSx(isDark)}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ color: isDark ? drawerTheme.text : '#0f172a', fontWeight: 700 }}
                      >
                        {event.title || 'Booking Activity'}
                      </Typography>
                      <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#64748b' }}>
                        {formatBookingAuditTime(event.created_at, event.created_at_display)}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ color: isDark ? drawerTheme.subtleText : '#475569', mt: 0.5 }}
                    >
                      {sanitizeBookingAuditText(event.description) || 'Booking activity captured.'}
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip size="small" label={`By ${actorLabel}`} sx={getBookingAuditActorChipSx(isDark)} />
                      {actorMeta ? (
                        <Chip size="small" label={actorMeta} sx={getBookingAuditMetaChipSx(isDark)} />
                      ) : null}
                      <Chip
                        size="small"
                        label={(event.event_type || 'activity').replace(/_/g, ' ')}
                        sx={getBookingAuditEventTypeChipSx(isDark, presentation.color)}
                      />
                    </Box>
                  </Paper>
                </Box>
              );
            })}
        </Box>
      </Box>
    </Drawer>
  );
};

export default BookingAuditDrawer;
